import React, { useEffect, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  FormFeedback,
  Container,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import { BackButton } from "components/common/Buttons";
import StatusBadge from "components/common/StatusBadge";
import { Enquiry } from "features/Enquiries/types/enquiry.types";
import {
  createEnquiry,
  getEnquiry,
  updateEnquiry,
} from "features/Enquiries/services/EnquiryService";

import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getClientOptions } from "features/Clients/services/ClientService";
import {
  getPriorityOptions,
  getSourceChannelOptions,
} from "features/Meta/services/MetaService";
import {
  EnquiryIntent,
  EnquiryIntentLabelMap,
  EnquiryStatus,
  enquiryStatusMap,
} from "features/Enquiries/constants/enquiry.constants";
import { getDocumentTitle } from "utils/helpers";

const EnquiryForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(!isEdit ? "New Enquiry" : "Edit Enquiry");

  const { id } = useParams();
  const [record, setRecord] = useState<Enquiry>();
  const navigate = useNavigate();
  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: record?.title ?? "",
      sourceChannelId: record?.sourceChannelId ?? "",
      description: record?.description ?? "",
      enquiryIntent: record?.enquiryIntent ?? "",
      priorityId: record?.priorityId ?? "",
      clientId: record?.clientId ?? "",
      clientName: record?.clientName ?? "",
      clientAddress: record?.clientAddress ?? "",
      clientPhone: record?.clientPhone ?? "",
      clientEmail: record?.clientEmail ?? "",
      status: isEdit ? (record?.status ?? "") : EnquiryStatus.NEW,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const enquiry = {
        title: values.title,
        sourceChannelId:
          values.sourceChannelId !== "" && values.sourceChannelId != null
            ? Number(values.sourceChannelId)
            : null,
        enquiryIntent: values.enquiryIntent as EnquiryIntent,
        description: values.description,
        priorityId: Number(values.priorityId),
        clientId: values.clientId ? Number(values.clientId) : null,
        clientName: values.clientName ?? "",
        clientAddress: values.clientAddress ?? "",
        clientPhone: values.clientPhone ?? "",
        clientEmail: values.clientEmail ?? "",
      };

      let response;
      try {
        if (isEdit) {
          response = await updateEnquiry(Number(id), enquiry);
        } else {
          response = await createEnquiry(enquiry);
        }

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        } else {
          toast.error(response.message);
        }
      } catch (err) {
        toast.error((err as any).response.data.message);
      }
    },
  });

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const response = await getEnquiry(Number(id));
        const { success, data } = response;
        if (success) {
          setRecord(data);
        }
      } catch (err) {
        console.warn(err);
      }
    };

    fetchRecord();
  }, [id]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit Enquiry" : "New Enquiry"}
            pageTitle="Enquiry Management"
            items={[
              { label: "Enquiries", path: "/enquiry-management/enquiries" },
            ]}
          />
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row className="justify-content-center">
              <Col>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      <h5 className="card-title mb-0">
                        {isEdit ? "Edit enquiry" : "Create enquiry"}
                      </h5>
                      {record ? (
                        <StatusBadge
                          value={record.status}
                          map={enquiryStatusMap}
                        />
                      ) : null}
                    </div>
                    <p className="text-muted mb-0 small">
                      Capture the enquiry's details and intent, link an
                      existing client or record their contact info directly.
                    </p>
                  </CardHeader>
                  <CardBody className="pt-4">
                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Details
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={12}>
                        <Label htmlFor="title" className="form-label">
                          Title
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Enter title"
                          id="title"
                          name="title"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.title || ""}
                          invalid={
                            validation.touched.title && validation.errors.title
                              ? true
                              : false
                          }
                        />
                        {validation.touched.title &&
                        validation.errors.title ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.title}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label">Priority</Label>
                        <AsyncPaginatedSelect
                          fetchOptions={getPriorityOptions}
                          placeholder="Search Priority..."
                          value={validation.values.priorityId}
                          onChange={(val: any) => {
                            validation.setFieldValue("priorityId", val?.value);
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("priorityId", true)
                          }
                          isInvalid={
                            !!validation.errors.priorityId &&
                            (validation.touched.priorityId ||
                              validation.submitCount > 0)
                          }
                        />
                        {validation.touched.priorityId &&
                        validation.errors.priorityId ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.priorityId}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label">Source channel</Label>
                        <AsyncPaginatedSelect
                          fetchOptions={getSourceChannelOptions}
                          placeholder="Search channel..."
                          defaultOption={
                            record?.sourceChannel
                              ? {
                                  value: record.sourceChannel.id,
                                  label: record.sourceChannel.name,
                                  code: record.sourceChannel.code,
                                }
                              : undefined
                          }
                          value={validation.values.sourceChannelId}
                          onChange={(val: any) => {
                            validation.setFieldValue(
                              "sourceChannelId",
                              val?.value ?? "",
                            );
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("sourceChannelId", true)
                          }
                          isInvalid={
                            !!validation.errors.sourceChannelId &&
                            (validation.touched.sourceChannelId ||
                              validation.submitCount > 0)
                          }
                        />
                        {validation.touched.sourceChannelId &&
                        validation.errors.sourceChannelId ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.sourceChannelId}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label">Enquiry Intent</Label>
                        <Input
                          className="form-select"
                          type="select"
                          name="enquiryIntent"
                          value={validation.values.enquiryIntent || ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.enquiryIntent &&
                            validation.errors.enquiryIntent
                              ? true
                              : false
                          }
                        >
                          <option>Choose...</option>
                          {Object.entries(EnquiryIntentLabelMap).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ),
                          )}
                        </Input>
                        {validation.touched.enquiryIntent &&
                        validation.errors.enquiryIntent ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.enquiryIntent}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={12}>
                        <Label htmlFor="description" className="form-label">
                          Description
                        </Label>
                        <Input
                          type="textarea"
                          className="form-control"
                          id="description"
                          rows={3}
                          name="description"
                          placeholder="Enter description"
                          value={validation.values.description || ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.description &&
                            validation.errors.description
                              ? true
                              : false
                          }
                        />
                        {validation.touched.description &&
                        validation.errors.description ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.description}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Client
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={6}>
                        <div className="d-flex align-items-center justify-content-between">
                          <Label className="form-label mb-0">
                            Select an existing client{" "}
                            <span className="text-muted fw-normal">
                              (optional)
                            </span>
                          </Label>
                          <a
                            href="/clients/create"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-link btn-sm p-0 mb-1"
                          >
                            <i className="ri-add-line align-bottom" /> New
                            client
                          </a>
                        </div>
                        <AsyncPaginatedSelect
                          defaultOption={
                            record?.client
                              ? {
                                  value: record.client.id,
                                  label:
                                    record.client.name?.trim() ||
                                    record.client.company?.trim() ||
                                    `Client #${record.client.id}`,
                                }
                              : undefined
                          }
                          fetchOptions={getClientOptions}
                          placeholder="Search Clients..."
                          value={validation.values.clientId}
                          onChange={(val: any) =>
                            validation.setFieldValue("clientId", val?.value)
                          }
                          onBlur={() =>
                            validation.setFieldTouched("clientId", true)
                          }
                          isInvalid={
                            validation.touched.clientId &&
                            validation.errors.clientId
                          }
                        />
                        {validation.touched.clientId &&
                        validation.errors.clientId ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.clientId}</div>
                          </FormFeedback>
                        ) : null}
                        <small className="text-muted d-block mt-1">
                          Leave blank and fill in the contact info below for a
                          walk-in or not-yet-registered client.
                        </small>
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Contact info
                    </div>
                    <p className="text-muted small mb-3">
                      The client's contact information for this enquiry.
                    </p>
                    <Row className="g-3 mb-2">
                      <Col md={12}>
                        <Label className="form-label">Full Name</Label>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Enter full name"
                          name="clientName"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.clientName || ""}
                          invalid={
                            validation.touched.clientName &&
                            validation.errors.clientName
                              ? true
                              : false
                          }
                        />
                        {validation.touched.clientName &&
                        validation.errors.clientName ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.clientName}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">Email</Label>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Enter email"
                          name="clientEmail"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.clientEmail || ""}
                          invalid={
                            validation.touched.clientEmail &&
                            validation.errors.clientEmail
                              ? true
                              : false
                          }
                        />
                        {validation.touched.clientEmail &&
                        validation.errors.clientEmail ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.clientEmail}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">Phone</Label>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Enter phone"
                          name="clientPhone"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.clientPhone || ""}
                          invalid={
                            validation.touched.clientPhone &&
                            validation.errors.clientPhone
                              ? true
                              : false
                          }
                        />
                        {validation.touched.clientPhone &&
                        validation.errors.clientPhone ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.clientPhone}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={12}>
                        <Label className="form-label">Address</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          className="form-control"
                          placeholder="Enter address"
                          name="clientAddress"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.clientAddress || ""}
                          invalid={
                            validation.touched.clientAddress &&
                            validation.errors.clientAddress
                              ? true
                              : false
                          }
                        />
                        {validation.touched.clientAddress &&
                        validation.errors.clientAddress ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.clientAddress}</div>
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>
                  </CardBody>
                  <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      className="btn-label px-4"
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          {isEdit ? "Updating…" : "Submitting…"}
                        </>
                      ) : isEdit ? (
                        "Save changes"
                      ) : (
                        "Create enquiry"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EnquiryForm;
